import useHeader from '@hooks/useHeader';
import { useTranslation } from 'react-i18next';
import EmailList from '../Components/EmailList';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useLoaderDeps, useNavigate } from '@tanstack/react-router';
import { emailsSentQueryOptions } from '@utils/queryOptions';
import { Box } from '@mui/material';
import Tag from '@Components/Common/Tag';
import {
    MagicClickOutlinedRounded,
    OpenedEmailOutlinedRounded,
    SendOutlinedRounded,
} from '@Components/Icons';
import useSimplePagination from '@hooks/useSimplePagination';
import useSimpleSort from '@hooks/useSimpleSort';
import Toolbar from '@Components/General/Toolbar';
import SimpleSort from '@Components/Common/SimpleSort';
import SimplePagination from '@Components/Common/SimplePagination';
import NoEmailsSent from './no-emails';
import useEmptySearchParams from '@hooks/useEmptySearchParams';
import NoEmailsSentFound from './no-emails-found';

const EmailStats = ({ stats }) => {
    // // set the stats to random numbers where the 'sent' is the highest, the 'opened' should be less than 'sent' and 'clicked' even less
    // const sent = Math.floor(Math.random() * 1000);
    // const opened = Math.floor(sent * Math.min(Math.random(), 0.7));
    // const clicked = Math.floor(opened * Math.min(Math.random(), 0.6));
    // stats = { sent, opened, clicked };

    return (
        <Box display="flex" gap={1}>
            <Tag
                label={stats?.sent?.toLocaleString()}
                color="secondary"
                size="medium"
                variant="tonal"
                icon={<SendOutlinedRounded />}
                sx={{ fontSize: 16 }}
            />
            <Tag
                label={stats?.opened?.toLocaleString()}
                color="secondary"
                size="medium"
                variant="tonal"
                icon={<OpenedEmailOutlinedRounded />}
                sx={{ fontSize: 16 }}
            />
            <Tag
                label={stats?.clicked?.toLocaleString()}
                color="secondary"
                size="medium"
                variant="tonal"
                icon={<MagicClickOutlinedRounded />}
                sx={{ fontSize: 16 }}
            />
        </Box>
    );
};

const EmailsSent = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const searchParamsEmpty = useEmptySearchParams();

    const simplePageHook = useSimplePagination();
    const simpleSortHook = useSimpleSort({
        inverseFields: ['sent_time'],
        defaultSortField: '-sent_time',
    });

    const headerOptions = useMemo(() => {
        return {
            title: t('Emails Sent'),
            fabAction: () => () => navigate({ to: 'drafts/new' }),
            searchProps: {
                searchPlacholder: t('search:emails-sent'),
            },
        };
    }, []);

    useHeader(headerOptions);

    const {
        data: { results, count },
    } = useSuspenseQuery(emailsSentQueryOptions(useLoaderDeps({})));

    if (results.length === 0 && searchParamsEmpty) {
        return <NoEmailsSent />;
    }

    return (
        <Box flexGrow={1}>
            <Box display="flex" flexDirection="column" height="100%">
                <Toolbar>
                    <SimpleSort
                        sortOptions={[
                            { value: 'sent_time', label: t('Last Sent') },
                            { value: 'subject', label: t('Subject') },
                        ]}
                        {...simpleSortHook}
                    />
                    <SimplePagination count={count} {...simplePageHook} />
                </Toolbar>
                {results.length === 0 ? (
                    <NoEmailsSentFound />
                ) : (
                    <EmailList
                        data={results}
                        metaText={item =>
                            `${t('Sent')}: ${item.sent_time.toContextualDateTime()}`
                        }
                        action={EmailStats}
                        secondaryPath="preview_text"
                    />
                )}
            </Box>
        </Box>
    );
};

export default EmailsSent;
